import React from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'antd';
import { FileOutlined } from '@ant-design/icons';
import { Button, Icon } from 'react-inv-forms';

import { Container } from './DynamicButtonContainer.style';

import fingerprint from '../../../images/fingerprint.svg';
import fingerprintGrey from '../../../images/fingerprintGrey.svg';
import penWhite from '../../../images/penWhite.svg';
import penGrey from '../../../images/penGrey.svg';

export interface Props {
  /** callback wykonywany na przycisku "Generuj Protokół" */
  onProtocolGeneration: () => void;
  /** callback wykonywany na przycisku "Podpisz Oceny" */
  onMarksSigning: () => void;
  /** callback wykonywany na przycisku "Przepisz oceny końcowe z ocen wyliczonych" */
  onFinalMarkCopy: () => void;
  /** translacje dla przycisków "Generuj Protokół", "Podpisz Oceny" i "Przepisz oceny końcowe z ocen wyliczonych" */
  translations: { [key: string]: React.ReactElement };
  /** informacja czy użytkownik ma pozwolenie na wykonanie akcji podpisania oceny i wygenerowania protokołu */
  canSigning?: boolean;
  /** informacja, czy mają zostać aktywne przyciski "Podpisz Oceny" i "Przepisz oceny końcowe z ocen wyliczonych" */
  signDisplay?: boolean;
  /** informacja, czy ma zostać wyświetlony przycisk "Generuj Protokół" */
  displayGenerateProtocol?: boolean;
  /** informacja, czy są zaznaczeni studenci */
  isStudentMarked?: boolean;
  /** informacja, czy tablica jest pusta */
  isTableEmpty?: boolean;
}
/**
 * Komponent dynamicznego kontenera przycisków
 * -   pozwala określić, które przyciski chcemy wyświetlić
 * -   wyświetla przyciski tylko jeśli użytkownik ma pozwolenie na wykonanie akcji (określone za pośrednictwem właściwosci canSigning)
 *
 */
export const DynamicButtonContainer: React.FunctionComponent<Props> = (props: Props) => {
  const { t } = useTranslation();

  return (
    <Container>
      {props.signDisplay ? (
        <>
          <Tooltip
            title={
              props.isTableEmpty
                ? null
                : !props.canSigning
                ? t('KP_CAN_NOT_SIGNING_NO_ACCESS')
                : !props.isStudentMarked
                ? t('KP_CAN_NOT_SIGNING_STUDENT_MARKS_FINAL_MARK')
                : null
            }
          >
            <span style={{ cursor: !props.canSigning || !props.isStudentMarked ? 'not-allowed' : 'inherit' }}>
              <Button
                disabled={!props.canSigning || !props.isStudentMarked}
                onClick={props.onFinalMarkCopy}
                style={{ pointerEvents: !props.canSigning || !props.isStudentMarked ? 'none' : 'inherit' }}
                data-cy='btnCopyFinalMarks'
              >
                <Icon path={!props.canSigning || !props.isStudentMarked ? penGrey : penWhite} />
                {props.translations.copyFinalMark}
              </Button>
            </span>
          </Tooltip>
          <Tooltip
            title={
              props.isTableEmpty
                ? null
                : !props.canSigning
                ? t('KP_CAN_NOT_SIGNING_NO_ACCESS')
                : !props.isStudentMarked
                ? t('KP_CAN_NOT_SIGNING_STUDENT_MARKS')
                : null
            }
          >
            <span style={{ cursor: !props.canSigning || !props.isStudentMarked ? 'not-allowed' : 'inherit' }}>
              <Button
                disabled={!props.canSigning || !props.isStudentMarked}
                onClick={props.onMarksSigning}
                style={{ pointerEvents: !props.canSigning || !props.isStudentMarked ? 'none' : 'inherit' }}
                data-cy='btnSignFinalMarks'
              >
                <Icon path={!props.canSigning || !props.isStudentMarked ? fingerprintGrey : fingerprint} />
                {props.translations.signMarks}
              </Button>
            </span>
          </Tooltip>
        </>
      ) : null}
      {props.displayGenerateProtocol ? (
        <Button outlined={true} onClick={props.onProtocolGeneration} disabled={props.isTableEmpty} data-cy='btnGenerateProtocol'>
          <FileOutlined />
          {props.translations.generateProtocol}
        </Button>
      ) : null}
    </Container>
  );
};

export default DynamicButtonContainer;
